// frontend/src/hooks/usePlatformProjects.ts — the sysadmin platform-projects read
// surface (list · detail · license-tier catalogue · offerable countries). Platform
// server state (sysadmin-only, ADR-018): keys are `['platform-projects', …]` so a
// create/update/lifecycle mutation invalidates the list + any open detail together.
// 401/403 is the centralized apiClient's job (ADR-003); these let the error bubble
// so the page's 5-state contract renders it.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  LicenseTier,
  LicenseTiersResponse,
  OfferableCountriesResponse,
  OfferableCountry,
  PlatformProjectDetail,
  PlatformProjectListItem,
  PlatformProjectsListResponse,
} from '@ems-app/schemas/projects/platform-responses';
import { apiClient } from '../lib/apiClient.js';

export const platformProjectsKeys = {
  /** List key — also the invalidation PREFIX (covers detail). */
  all: ['platform-projects'] as const,
  detail: (id: string) => ['platform-projects', id] as const,
  tiers: ['platform-projects', 'license-tiers'] as const,
  countries: ['platform-projects', 'countries'] as const,
};

const selectItems = (r: PlatformProjectsListResponse): PlatformProjectListItem[] => r.items;

export function usePlatformProjects(): UseQueryResult<PlatformProjectListItem[], Error> {
  return useQuery({
    queryKey: platformProjectsKeys.all,
    queryFn: async () => (await apiClient.get<PlatformProjectsListResponse>('/platform/projects')).data,
    select: selectItems,
  });
}

/** The project's detail read. `quiet` — a drawer's Load the latest: a project gone meanwhile answers the drawer,
 *  never the not-found page (ADR-084 §6). */
export async function fetchPlatformProject(projectId: string, quiet = false): Promise<PlatformProjectDetail> {
  return (await apiClient.get<PlatformProjectDetail>(`/platform/projects/${projectId}`, quiet ? { quietNotFound: true } : undefined)).data;
}

export function usePlatformProject(projectId: string): UseQueryResult<PlatformProjectDetail, Error> {
  return useQuery({
    queryKey: platformProjectsKeys.detail(projectId),
    queryFn: () => fetchPlatformProject(projectId),
    enabled: projectId.length > 0,
  });
}

const selectTiers = (r: LicenseTiersResponse): LicenseTier[] => r.items;

/** The catalogue's read — the list's, and a tier form's Load the latest (a tier has no detail read: the list row is
 *  its read). */
export async function fetchLicenseTiers(): Promise<LicenseTiersResponse> {
  return (await apiClient.get<LicenseTiersResponse>('/platform/license-tiers')).data;
}

/** The global license-tier catalogue (seed-managed). Consumed by the create wizard
 *  + the manage-licensing drawer, which read it and never assume a fixed count. */
export function useLicenseTiers(): UseQueryResult<LicenseTier[], Error> {
  return useQuery({
    queryKey: platformProjectsKeys.tiers,
    queryFn: fetchLicenseTiers,
    select: selectTiers,
    staleTime: 5 * 60_000, // the catalogue rarely changes
  });
}

const selectCountries = (r: OfferableCountriesResponse): OfferableCountry[] => r.items;

/** Offerable countries + their default timezone — the create/countries picker source. */
export function useOfferableCountries(): UseQueryResult<OfferableCountry[], Error> {
  return useQuery({
    queryKey: platformProjectsKeys.countries,
    queryFn: async () => (await apiClient.get<OfferableCountriesResponse>('/platform/countries')).data,
    select: selectCountries,
    staleTime: 5 * 60_000,
  });
}
